import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmMakeClient, FirearmMakeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmMakeClient = () => new FirearmMakeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmMakes = () =>
  useQuery({
    queryKey: ['firearmMakes'],
    queryFn: async () => {
      const response = await getFirearmMakeClient().searchFirearmMakes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmMakeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmMakeClient().searchFirearmMakes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmMakeById = (id: string) =>
  useQuery({
    queryKey: ['firearmMake', id],
    queryFn: async () => {
      const response = await getFirearmMakeClient().getFirearmMakeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmMakeDTO) => {
      await getFirearmMakeClient().createFirearmMake(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmMakes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmMakeDTO }) => {
      await getFirearmMakeClient().updateFirearmMake(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmMakes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmMakeClient().deleteFirearmMake(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmMakes'] });
    },
  });
};